import { scoreBarColor, scoreColor } from '@/lib/scoreColors'
import { cn } from '@/lib/utils'
import type { StarScores } from '@/types/interview'

interface StarScoreCardProps {
  scores: StarScores
  className?: string
}

const COMPONENTS: {
  key: keyof Omit<StarScores, 'overall' | 'competencyScore'>
  label: string
  weight: string
}[] = [
  { key: 'situation', label: 'Situation', weight: '20%' },
  { key: 'task', label: 'Task', weight: '15%' },
  { key: 'action', label: 'Action', weight: '40%' },
  { key: 'result', label: 'Result', weight: '25%' },
]

export function StarScoreCard({ scores, className }: StarScoreCardProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {/* Overall score */}
      <div className="rounded-2xl border border-border bg-muted/40 p-6 flex items-center gap-6">
        <div className="text-center min-w-[72px]">
          <p className={cn('text-5xl font-bold tabular-nums', scoreColor(scores.overall))}>
            {scores.overall.toFixed(1)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">out of 5.0</p>
        </div>
        <div className="flex-1 space-y-1">
          <p className="text-sm font-semibold">Overall STAR score</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Weighted average across Situation, Task, Action, and Result.
          </p>
        </div>
      </div>

      {/* Per-component breakdown */}
      <div className="rounded-xl border border-border bg-background divide-y divide-border overflow-hidden">
        {COMPONENTS.map(({ key, label, weight }) => {
          const score = scores[key]
          const pct = ((score - 1) / 4) * 100
          return (
            <div key={key} className="flex items-center gap-4 px-4 py-3">
              <div className="w-28 flex-shrink-0">
                <span className="text-sm font-medium">{label}</span>
                <span className="ml-1.5 text-xs text-muted-foreground">({weight})</span>
              </div>
              <div
                role="progressbar"
                tabIndex={-1}
                aria-valuenow={score}
                aria-valuemin={1}
                aria-valuemax={5}
                aria-label={`${label} score`}
                className="flex-1 h-2 rounded-full bg-muted overflow-hidden"
              >
                <div
                  className={cn('h-full rounded-full transition-all', scoreBarColor(score))}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span
                className={cn(
                  'w-8 text-right text-sm font-semibold tabular-nums',
                  scoreColor(score)
                )}
              >
                {score.toFixed(1)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
